import React from "react"
import { Provider } from "../../types/Providers"
import { useUserContext } from "../../context/UserContext"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { useIntl } from "react-intl"
import { useGetLocalizedDistanceStringWithUnits } from "#/pages/provider-finder/hooks/useGetLocalizedDistanceStringWithUnits"
import { Box, LocationIcon, Typography, VirtualIcon } from "@carrotfertility/carotene-core"
import { ProviderDetailsMainContent } from "#/pages/provider-finder/components/results/ProviderDetailsMainContent"

function DistanceOrVirtualDisplay({ distanceInKm, address }: { distanceInKm: number; address: string }): JSX.Element {
  const intl = useIntl()
  const { companyCountryCode } = useUserContext()
  const localizedProviderDistance = useGetLocalizedDistanceStringWithUnits({
    distanceInKm,
    countryCode: companyCountryCode,
    numberOfFractionDigits: 1
  })
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "stretch",
        justifyContent: ["flex-start", null, "center"],
        minInlineSize: 90
      }}
      gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
      aria-label={distanceInKm ? localizedProviderDistance : intl.formatMessage({ defaultMessage: "Virtual provider" })}
    >
      <Box
        component="span"
        sx={(theme) => ({
          color: theme.palette.text.tertiary,
          display: "flex",
          alignItems: "center"
        })}
        gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
      >
        {distanceInKm ? (
          <>
            <LocationIcon fontSize="inherit" role="presentation" color="inherit" />
            <Typography variant="body2" color="inherit">
              {localizedProviderDistance}
            </Typography>
          </>
        ) : (
          !address && <VirtualIcon fontSize="medium" color="inherit" />
        )}
      </Box>
    </Box>
  )
}

type ProviderDetailsProps = {
  provider: Provider
  rank?: number
  searchLocation: string
  benefit: Benefit
}

export function ProviderDetails({ provider, rank, searchLocation, benefit }: ProviderDetailsProps): JSX.Element {
  const { address, distanceInKm, isVirtual } = provider

  return (
    <Box
      sx={{
        display: "flex",
        inlineSize: "100%",
        justifyContent: "space-between",
        flexDirection: ["column", "row", "row", "row"]
      }}
      gap={(theme) => [
        theme.spacing(theme.tokens.spacing.md),
        theme.spacing(theme.tokens.spacing.sm),
        theme.spacing(theme.tokens.spacing.sm),
        theme.spacing(theme.tokens.spacing.md)
      ]}
      data-provider-search-result-rank={rank}
      data-provider-uuid={provider.providerUuid}
      data-is-virtual-provider={Boolean(isVirtual)}
      data-provider-has-address={Boolean(address)}
    >
      <ProviderDetailsMainContent {...{ provider, benefit, searchLocation }} />
      <DistanceOrVirtualDisplay {...{ distanceInKm, address }} />
    </Box>
  )
}
